/**
 * @fileoverview Backward-compatible wrapper around ProductImportTable for Baselinker imports.
 * @module features/baselinker-import/components/BaselinkerProductsTable
 */

import { useMemo, type ReactElement } from 'react'

import {
  ProductImportTable,
  type ImportProductItem,
  type ImportVariantItem,
  type ProductImportTableProps,
} from '@/shared/components/ProductImportTable'
import type { ProductPreviewItem, BaselinkerVariant } from '../schemas/baselinkerImportSchema'

export interface BaselinkerProductsTableProps
  extends Omit<ProductImportTableProps, 'products' | 'selectedVariantIds' | 'onVariantSelectionChange' | 'sourceName' | 'getVariantIdentifier'> {
  /** Products to display */
  products: ProductPreviewItem[]
  /** Currently selected variant SKUs (individual variants) */
  selectedVariantSkus: Set<string>
  /** Callback when variant selection changes */
  onVariantSelectionChange: (skus: Set<string>) => void
}

/**
 * Converts a Baselinker variant to the generic ImportVariantItem format.
 */
function toImportVariant(variant: BaselinkerVariant): ImportVariantItem {
  return {
    variantId: variant.variantId,
    name: variant.name,
    sku: variant.sku,
    alreadyExists: variant.alreadyExists ?? false,
  }
}

/**
 * Converts a Baselinker product to the generic ImportProductItem format.
 */
function toImportProduct(product: ProductPreviewItem): ImportProductItem {
  return {
    externalProductId: product.baselinkerProductId,
    productName: product.productName,
    imageUrl: product.imageUrl ?? null,
    sku: product.sku,
    skuAlreadyExists: product.skuAlreadyExists,
    variantCount: product.variants?.length ?? 0,
    variants: product.variants?.map(toImportVariant) ?? null,
    averageCost: product.averageCost ?? null,
    siblingConflict: product.siblingConflict ?? false,
    siblingConflictDetails: product.siblingConflictDetails ?? null,
  }
}

/**
 * Backward-compatible wrapper for Baselinker product import table.
 * Converts Baselinker-specific types to the generic ProductImportTable format.
 */
export function BaselinkerProductsTable({
  products,
  selectedVariantSkus,
  onVariantSelectionChange,
  ...rest
}: BaselinkerProductsTableProps): ReactElement {
  // Convert Baselinker products to generic format
  const genericProducts = useMemo(
    () => products.map(toImportProduct),
    [products]
  )

  return (
    <ProductImportTable
      {...rest}
      products={genericProducts}
      selectedVariantIds={selectedVariantSkus}
      onVariantSelectionChange={onVariantSelectionChange}
      sourceName="Baselinker"
      getVariantIdentifier={(v) => v.sku ?? v.variantId}
    />
  )
}
